import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import { RecordRefSchema } from './notification.ts';

/**
 * A request for information (SCRUM-144, P8-11).
 *
 * `docs/features/13-later-modules.md`: "Question/answer threads tied to
 * COs, selections, schedule; due dates; counts on CO list."
 *
 * **An RFI is a question somebody is blocked on.** That is the whole
 * shape of it: it has a due date because the answer is holding work up,
 * it points at the record it is about, and it is either waiting or it is
 * not. Everything below follows.
 *
 * **No number.** Change orders and invoices carry a job-prefixed sequence
 * because they are instruments somebody signs and accounts for, and the
 * number is their identity; that costs a server transaction against a
 * counter. The spec asks for threads, due dates and a count, and names no
 * number — so there is none, and adding one later is a migration rather
 * than a field. Recorded in `docs/features/19-rfis.md` so the absence is
 * a decision rather than an oversight.
 */
export const RFI_STATUSES = ['open', 'answered', 'closed'] as const;
export const RfiStatusSchema = z.enum(RFI_STATUSES);
export type RfiStatus = z.infer<typeof RfiStatusSchema>;

export const RFI_STATUS_LABEL: Record<RfiStatus, string> = {
  open: 'Waiting',
  answered: 'Answered',
  closed: 'Closed',
};

export const RfiAnswerSchema = z.object({
  text: z.string().trim().min(1).max(20_000),
  at: InstantSchema,
  byUid: IdSchema,
});
export type RfiAnswer = z.infer<typeof RfiAnswerSchema>;

export const RfiSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  subject: z.string().trim().min(1, 'An RFI needs a subject').max(200),
  question: z.string().trim().max(20_000).default(''),
  status: RfiStatusSchema,
  /**
   * When an answer is needed by.
   *
   * Optional, because a question asked on the way past is still worth
   * recording and demanding a date would stop somebody asking it. A
   * calendar date rather than an instant: "Thursday" is what anybody
   * means, and a time zone argument about a deadline helps nobody.
   */
  dueDate: DateOnlySchema.optional(),
  askedBy: IdSchema,
  askedAt: InstantSchema,
  /**
   * The trade the question is put to, when it is put to one.
   *
   * A directory contact rather than a member, like a warranty claim's
   * assignment: the architect or the engineer answering may have no
   * login, and the rules gate an external member's sight of the RFI on
   * this field as a scalar.
   */
  directedToContactId: IdSchema.optional(),
  answer: RfiAnswerSchema.optional(),
  linkedRecords: z.array(RecordRefSchema).default([]),
  linkedKeys: z.array(z.string().min(1)).default([]),
  version: z.number().int().positive(),
});
export type Rfi = z.infer<typeof RfiSchema>;

/**
 * Where an RFI may go next.
 *
 * `answered` can return to `open`, which is the one edge worth arguing
 * about and the one that happens: an answer that does not answer the
 * question leaves somebody still blocked, and making them raise a second
 * RFI would lose the first one's thread and its due date. `closed` is
 * terminal, as everywhere else in this product.
 */
export const RFI_TRANSITIONS: Record<RfiStatus, readonly RfiStatus[]> = {
  open: ['answered', 'closed'],
  answered: ['open', 'closed'],
  closed: [],
};

export function canTransitionRfi(from: RfiStatus, to: RfiStatus): boolean {
  return RFI_TRANSITIONS[from].includes(to);
}

export function isRfiWaiting(rfi: Pick<Rfi, 'status'>): boolean {
  return rfi.status === 'open';
}

/**
 * Waiting, and the date has passed.
 *
 * An **answered** RFI is never overdue however old its due date, because
 * the thing the date measured has happened. A list that kept flagging it
 * would be counting history.
 */
export function isRfiOverdue(rfi: Pick<Rfi, 'status' | 'dueDate'>, today: string): boolean {
  return isRfiWaiting(rfi) && rfi.dueDate !== undefined && rfi.dueDate < today;
}

/**
 * How many questions are outstanding against one record.
 *
 * The count the change-order list shows. **Waiting only** — a change
 * order with three answered RFIs has nothing outstanding, and a badge
 * saying "3" beside it would send somebody looking for work that is
 * already done.
 */
export function waitingAgainst(rfis: readonly Rfi[], linkKey: string): number {
  return rfis.filter((rfi) => isRfiWaiting(rfi) && rfi.linkedKeys.includes(linkKey)).length;
}

/**
 * The order a list of RFIs is read in.
 *
 * Waiting first, then by due date with the undated last, then oldest
 * first. Somebody opening this list is looking for what is holding work
 * up, and the sort says so: the overdue question asked three weeks ago is
 * at the top, and the answered ones are out of the way without being
 * hidden.
 */
export function inAnswerOrder(rfis: readonly Rfi[]): Rfi[] {
  return [...rfis].sort((a, b) => {
    if (isRfiWaiting(a) !== isRfiWaiting(b)) return isRfiWaiting(a) ? -1 : 1;
    if (a.dueDate !== b.dueDate) {
      if (a.dueDate === undefined) return 1;
      if (b.dueDate === undefined) return -1;
      return a.dueDate.localeCompare(b.dueDate);
    }
    return a.askedAt.localeCompare(b.askedAt) || a.id.localeCompare(b.id);
  });
}
